import { TableFooter } from '@fukuri-admins/features/Table';
import { TableSearch } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useClearableFilters } from '@fukuri-admins/utils/common';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getRedeemableStoreCouponHistories } from '../../../apis/redeemableStoreCouponHistories/index';
import { useAuth } from '../../../components/auth/AuthProvider.tsx';
import { ContentsHeader } from '../../../components/common/ContentsHeader/index.tsx';
import { RedeemableStoreCouponHistoryMobileCardList } from '../../../components/redeemableStoreCouponHistory/MobileCardList/index';
import { RedeemableStoreCouponHistoryTableContent } from '../../../components/redeemableStoreCouponHistory/TableContent/index.tsx';
import { RedeemableStoreCouponHistoryTableFilter } from '../../../components/redeemableStoreCouponHistory/TableFilter/index.tsx';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { RedeemableStoreCouponHistoryTableData } from '../../../types/redeemableStoreCouponHistory/RedeemableStoreCouponHistoryTableData';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType.ts';
import { convertDateTime } from '../../../utils/dateTime';
import { handleSorting } from '../../../utils/sortings.ts';
import styles from './styles.module.scss';

export const IndexRedeemableStoreCouponHistory: React.FC = () => {
  const isMobile = useIsMobile();
  const [filteredRedeemableStoreCouponHistories, setFilteredRedeemableStoreCouponHistories] =
    useState<RedeemableStoreCouponHistoryTableData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { signOut } = useAuth();
  const [filterParams, setFilterParams] = useSearchParams();
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });

  const { showToast } = useToast();

  const getRedeemableStoreCouponHistoriesData = async (page?: number) => {
    try {
      const branch = filterParams.get('branch');
      const issue_identity = filterParams.get('issue_identity');
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getRedeemableStoreCouponHistories(signOut, {
        ...(branch && { b: branch }),
        ...(issue_identity && { i: issue_identity }),
        ...(pageNumber && { page: pageNumber.toString() }),
      });

      updatePaginationInfo(headers);

      const processedUsers = data.redeemable_store_coupons.map(redeemable_store_coupon => {
        return {
          id: redeemable_store_coupon.id,
          amount: redeemable_store_coupon.amount,
          issue_identity: '', // APIからデータが返ってこないので値をセットしない
          branch: redeemable_store_coupon.store.branch,
          redeemed_at: convertDateTime(redeemable_store_coupon.redeemed_at),
          three_letter_code: redeemable_store_coupon.three_letter_code,
          is_cancelled: redeemable_store_coupon.is_cancelled,
        };
      });
      setFilteredRedeemableStoreCouponHistories(processedUsers);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const { setIsClearFilter, filterKey } = useClearableFilters({
    filterParams,
    fetchFunction: getRedeemableStoreCouponHistoriesData,
  });

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    getRedeemableStoreCouponHistoriesData(page);
  };

  const updatePaginationInfo = (headers: unknown) => {
    setTotalPage(Number(headers['total-pages']));
    setTotalCount(Number(headers['total-count']));
  };

  const onSearch = () => {
    setIsLoading(true);
    getRedeemableStoreCouponHistoriesData();
  };

  const onClear = () => {
    setIsLoading(true);
    setFilterParams(new URLSearchParams());
    setIsClearFilter(true);
  };

  useEffect(() => {
    getRedeemableStoreCouponHistoriesData();
  }, []);

  return (
    <div className={styles.Root} data-testid='clients-page'>
      <ContentsHeader title='地域ギフト利用履歴' />
      <div className={styles.tableContainer}>
        <div>
          {!isMobile && (
            <div className={styles.searchContainer}>
              <TableSearch onSearch={onSearch} onClear={onClear}>
                <RedeemableStoreCouponHistoryTableFilter key={filterKey} />
              </TableSearch>
            </div>
          )}
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              {isMobile ? (
                <RedeemableStoreCouponHistoryMobileCardList
                  rows={filteredRedeemableStoreCouponHistories}
                  setIsLoading={setIsLoading}
                />
              ) : (
                <RedeemableStoreCouponHistoryTableContent
                  rows={filteredRedeemableStoreCouponHistories}
                  sortParams={sortParams}
                  setIsLoading={setIsLoading}
                  headerSort={column =>
                    handleSorting(
                      column,
                      sortParams,
                      filteredRedeemableStoreCouponHistories,
                      setSortParams,
                      setFilteredRedeemableStoreCouponHistories
                    )
                  }
                />
              )}
              <TableFooter
                onPageChange={handlePageChange}
                totalCount={totalCount}
                totalPage={totalPage}
                itemCount={filteredRedeemableStoreCouponHistories.length}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
